import { useEffect, useMemo, useState } from 'react';
import { X, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { US_STATES } from '@/constants/usStates';
import { formatDateTimeMdY } from '@/utils/dateFormat';
import { adminDialogPanel, adminIconBtn, adminOverlay } from '@/components/admin/adminUi';
import { LEAD_TYPE_LABELS } from '@/constants/adminVocabulary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'pending', label: 'Pending' },
  { value: 'sold', label: 'Sold' },
];

const emptyCustomer = () => ({
  purchase_type: 'cash',
  sale_price: '',
  customer_name: '',
  street_address: '',
  city: '',
  state: 'IL',
  zip: '',
  dl_number: '',
  dl_issuing_county: '',
  dl_issuing_state: 'IL',
  dl_expiry: '',
  date_of_birth: '',
  phone: '',
  email: '',
  lien_holder_id: '',
});

const inputCls =
  "w-full bg-white/5 border border-white/10 text-white px-3 py-2 font-['Manrope'] text-sm focus:outline-none focus:border-blue-500/50";
const labelCls = "block text-xs font-['Manrope'] text-white/60 mb-1.5";

export default function VehicleStatusChangeModal({
  isOpen,
  vehicle,
  token,
  mode = 'inventory',
  onClose,
  onSuccess,
}) {
  const [step, setStep] = useState('status');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [purchaseType, setPurchaseType] = useState('cash');
  const [customer, setCustomer] = useState(emptyCustomer());
  const [lienHolders, setLienHolders] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadSearch, setLeadSearch] = useState('');
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [deals, setDeals] = useState([]);
  const [selectedDealId, setSelectedDealId] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    if (!isOpen || !vehicle) return;
    setStep('status');
    setSelectedStatus('');
    setPurchaseType('cash');
    setCustomer(emptyCustomer());
    setLeadSearch('');
    setSelectedLeadId('');
    setDeals([]);
    setSelectedDealId('');
    setSalePrice(vehicle?.price ? String(vehicle.price) : '');
  }, [isOpen, vehicle]);

  useEffect(() => {
    if (!isOpen || !token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/admin/lien-holders`, { headers });
        if (!cancelled) setLienHolders(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setLienHolders([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, token, headers]);

  if (!isOpen || !vehicle) return null;

  const visibleStatuses = mode === 'sold'
    ? STATUS_OPTIONS.filter((s) => s.value !== 'sold')
    : STATUS_OPTIONS;

  const filteredLeads = leads.filter((lead) => {
    const q = leadSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      (lead.name || '').toLowerCase().includes(q) ||
      (lead.email || '').toLowerCase().includes(q) ||
      (lead.phone || '').toLowerCase().includes(q) ||
      (lead.type || '').toLowerCase().includes(q)
    );
  });

  const postStatus = async (payload) => {
    const { data } = await axios.post(
      `${BACKEND_URL}/api/admin/vehicles/${vehicle.id}/status`,
      payload,
      { headers }
    );
    return data;
  };

  const applySimpleStatus = async (status) => {
    setSubmitting(true);
    try {
      await postStatus({ status });
      toast.success(`Vehicle status updated to ${status}`);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePickStatus = async (status) => {
    setSelectedStatus(status);
    if (status === 'sold') {
      setStep('sold-choice');
      return;
    }
    await applySimpleStatus(status);
  };

  const parsedSalePrice = () => {
    const n = Number(salePrice);
    return Number.isFinite(n) && n >= 0 ? n : null;
  };

  const openDealPicker = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/admin/vehicles/${vehicle.id}/linked-deals`,
        { headers }
      );
      const list = Array.isArray(data) ? data : [];
      setDeals(list);
      if (!list.length) {
        toast.error('No deals are linked to this vehicle');
        return;
      }
      setStep('link-deal');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load deals');
    } finally {
      setSubmitting(false);
    }
  };

  const openLeadPicker = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/sale-leads`, { headers });
      setLeads(Array.isArray(data) ? data : []);
      setStep('link-lead');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load leads');
    } finally {
      setSubmitting(false);
    }
  };

  const submitManualSale = async (e) => {
    e.preventDefault();
    if (!customer.customer_name.trim()) {
      toast.error('Customer full name is required');
      return;
    }
    const price = Number(customer.sale_price);
    if (!Number.isFinite(price) || price < 0 || customer.sale_price === '') {
      toast.error('Sale price is required');
      return;
    }
    setSubmitting(true);
    try {
      await postStatus({
        status: 'sold',
        source: 'manual',
        purchase_type: customer.purchase_type,
        sale_price: price,
        customer: {
          ...customer,
          sale_price: price,
          lien_holder_id: customer.purchase_type === 'financing' ? (customer.lien_holder_id || null) : null,
        },
      });
      toast.success('Vehicle marked as sold');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark vehicle sold');
    } finally {
      setSubmitting(false);
    }
  };

  const submitLinkedLead = async () => {
    if (!selectedLeadId) {
      toast.error('Select a lead to link');
      return;
    }
    const price = parsedSalePrice();
    if (price === null) {
      toast.error('Sale price is required');
      return;
    }
    setSubmitting(true);
    try {
      await postStatus({
        status: 'sold',
        source: 'lead',
        lead_id: selectedLeadId,
        purchase_type: purchaseType,
        sale_price: price,
        lien_holder_id: purchaseType === 'financing' ? (customer.lien_holder_id || null) : null,
      });
      toast.success('Vehicle marked as sold and linked to the lead');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link lead');
    } finally {
      setSubmitting(false);
    }
  };

  const submitLinkedDeal = async () => {
    if (!selectedDealId) {
      toast.error('Select a deal to link');
      return;
    }
    setSubmitting(true);
    try {
      await postStatus({
        status: 'sold',
        source: 'deal',
        deal_id: selectedDealId,
      });
      toast.success('Vehicle marked as sold and linked to the deal');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link deal');
    } finally {
      setSubmitting(false);
    }
  };

  const title = {
    status: 'Change vehicle status',
    'sold-choice': 'How should this sale be recorded?',
    'add-customer': 'Add customer info',
    'link-lead': 'Link customer from leads',
    'link-deal': 'Link customer from deal',
  }[step];

  return (
    <div className={adminOverlay()}>
      <div className={adminDialogPanel('max-w-xl')}>
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0a0a0a] z-10">
          <div>
            <h2 className="text-lg font-['Unbounded'] font-bold text-white">{title}</h2>
            <p className="text-xs font-['Manrope'] text-white/40 mt-1">
              {vehicle.year} {vehicle.make} {vehicle.model}
            </p>
          </div>
          <button type="button" onClick={onClose} className={adminIconBtn} aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {step === 'status' && (
            <div className="space-y-2">
              <p className="text-sm font-['Manrope'] text-white/70">What kind of status change is required?</p>
              {visibleStatuses.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  disabled={submitting || opt.value === vehicle.status}
                  onClick={() => handlePickStatus(opt.value)}
                  className={`w-full text-left px-4 py-3 border font-['Manrope'] ${
                    opt.value === vehicle.status
                      ? 'border-white/10 text-white/30 cursor-not-allowed'
                      : 'border-white/15 text-white hover:bg-white/5'
                  }`}
                >
                  {opt.label}
                  {opt.value === vehicle.status ? ' (current)' : ''}
                </button>
              ))}
            </div>
          )}

          {step === 'sold-choice' && (
            <div className="space-y-3">
              <button
                type="button"
                disabled={submitting}
                onClick={openLeadPicker}
                className="w-full px-4 py-3 border border-white/15 text-white font-['Manrope'] hover:bg-white/5"
              >
                1. Link Customer From Leads
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={() => {
                  setCustomer({
                    ...emptyCustomer(),
                    sale_price: vehicle.price ? String(vehicle.price) : '',
                  });
                  setStep('add-customer');
                }}
                className="w-full px-4 py-3 border border-white/15 text-white font-['Manrope'] hover:bg-white/5"
              >
                2. Add Customer Info
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={openDealPicker}
                className="w-full px-4 py-3 border border-white/15 text-white font-['Manrope'] hover:bg-white/5"
              >
                3. Link Customer From Deal
              </button>
              <button
                type="button"
                onClick={() => setStep('status')}
                className="text-sm text-white/50 font-['Manrope'] hover:text-white"
              >
                Back
              </button>
            </div>
          )}

          {step === 'add-customer' && (
            <form onSubmit={submitManualSale} className="space-y-4">
              <div>
                <label className={labelCls}>Purchase Type *</label>
                <select
                  value={customer.purchase_type}
                  onChange={(e) => setCustomer({ ...customer, purchase_type: e.target.value, lien_holder_id: '' })}
                  className={`${inputCls} [&>option]:bg-gray-900`}
                >
                  <option value="cash">Cash</option>
                  <option value="financing">Financing</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Sale Price *</label>
                <input
                  type="number"
                  min="0"
                  required
                  value={customer.sale_price}
                  onChange={(e) => setCustomer({ ...customer, sale_price: e.target.value })}
                  className={inputCls}
                  placeholder="e.g. 18500"
                />
              </div>
              <div>
                <label className={labelCls}>Customer Full Name *</label>
                <input
                  value={customer.customer_name}
                  onChange={(e) => setCustomer({ ...customer, customer_name: e.target.value })}
                  required
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Street Address</label>
                <input
                  value={customer.street_address}
                  onChange={(e) => setCustomer({ ...customer, street_address: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="col-span-3 sm:col-span-1">
                  <label className={labelCls}>City</label>
                  <input value={customer.city} onChange={(e) => setCustomer({ ...customer, city: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>State</label>
                  <select value={customer.state} onChange={(e) => setCustomer({ ...customer, state: e.target.value })} className={`${inputCls} [&>option]:bg-gray-900`}>
                    <option value="">—</option>
                    {US_STATES.map((st) => <option key={st} value={st}>{st}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>ZIP</label>
                  <input value={customer.zip} onChange={(e) => setCustomer({ ...customer, zip: e.target.value })} className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>DL Number</label>
                  <input value={customer.dl_number} onChange={(e) => setCustomer({ ...customer, dl_number: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>DL Issuing County</label>
                  <input value={customer.dl_issuing_county} onChange={(e) => setCustomer({ ...customer, dl_issuing_county: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>DL Issuing State</label>
                  <select value={customer.dl_issuing_state} onChange={(e) => setCustomer({ ...customer, dl_issuing_state: e.target.value })} className={`${inputCls} [&>option]:bg-gray-900`}>
                    <option value="">—</option>
                    {US_STATES.map((st) => <option key={st} value={st}>{st}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>DL Expiring Date</label>
                  <input type="date" value={customer.dl_expiry} onChange={(e) => setCustomer({ ...customer, dl_expiry: e.target.value })} className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Date of Birth</label>
                <input type="date" value={customer.date_of_birth} onChange={(e) => setCustomer({ ...customer, date_of_birth: e.target.value })} className={inputCls} />
              </div>
              {customer.purchase_type === 'financing' && (
                <div>
                  <label className={labelCls}>Lien Holder</label>
                  <select
                    value={customer.lien_holder_id}
                    onChange={(e) => setCustomer({ ...customer, lien_holder_id: e.target.value })}
                    className={`${inputCls} [&>option]:bg-gray-900`}
                  >
                    <option value="">Select lien holder…</option>
                    {lienHolders.map((h) => (
                      <option key={h.id} value={h.id}>{h.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Phone</label>
                  <input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Email</label>
                  <input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className={inputCls} />
                </div>
              </div>
              <div className="flex justify-between pt-2">
                <button type="button" onClick={() => setStep('sold-choice')} className="text-sm text-white/50 font-['Manrope']">
                  Back
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white font-['Manrope'] font-semibold disabled:opacity-50"
                >
                  {submitting ? 'Saving…' : 'Mark as Sold'}
                </button>
              </div>
            </form>
          )}

          {step === 'link-lead' && (
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Purchase Type *</label>
                <select
                  value={purchaseType}
                  onChange={(e) => {
                    setPurchaseType(e.target.value);
                    setCustomer({ ...customer, purchase_type: e.target.value, lien_holder_id: '' });
                  }}
                  className={`${inputCls} [&>option]:bg-gray-900`}
                >
                  <option value="cash">Cash</option>
                  <option value="financing">Financing</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Sale Price *</label>
                <input
                  type="number"
                  min="0"
                  required
                  value={salePrice}
                  onChange={(e) => setSalePrice(e.target.value)}
                  className={inputCls}
                  placeholder="e.g. 18500"
                />
              </div>
              {purchaseType === 'financing' && (
                <div>
                  <label className={labelCls}>Lien Holder</label>
                  <select
                    value={customer.lien_holder_id}
                    onChange={(e) => setCustomer({ ...customer, lien_holder_id: e.target.value })}
                    className={`${inputCls} [&>option]:bg-gray-900`}
                  >
                    <option value="">Select lien holder…</option>
                    {lienHolders.map((h) => (
                      <option key={h.id} value={h.id}>{h.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  value={leadSearch}
                  onChange={(e) => setLeadSearch(e.target.value)}
                  placeholder="Search leads by name, email, or phone"
                  className={`${inputCls} pl-9`}
                />
              </div>
              <div className="max-h-64 overflow-y-auto border border-white/10 divide-y divide-white/10">
                {filteredLeads.length === 0 ? (
                  <p className="p-4 text-sm text-white/40 font-['Manrope']">No matching leads</p>
                ) : (
                  filteredLeads.map((lead) => (
                    <button
                      key={lead.id}
                      type="button"
                      onClick={() => setSelectedLeadId(lead.id)}
                      className={`w-full text-left px-3 py-2.5 font-['Manrope'] ${
                        selectedLeadId === lead.id ? 'bg-blue-500/20' : 'hover:bg-white/5'
                      }`}
                    >
                      <div className="text-sm text-white">{lead.name || 'Unnamed lead'}</div>
                      <div className="text-xs text-white/45">
                        {LEAD_TYPE_LABELS[lead.type] || lead.type}
                        {lead.phone ? ` · ${lead.phone}` : ''}
                        {lead.email ? ` · ${lead.email}` : ''}
                        {lead.created_at ? ` · ${formatDateTimeMdY(lead.created_at)}` : ''}
                      </div>
                    </button>
                  ))
                )}
              </div>
              <div className="flex justify-between pt-2">
                <button type="button" onClick={() => setStep('sold-choice')} className="text-sm text-white/50 font-['Manrope']">
                  Back
                </button>
                <button
                  type="button"
                  disabled={submitting || !selectedLeadId}
                  onClick={submitLinkedLead}
                  className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white font-['Manrope'] font-semibold disabled:opacity-50"
                >
                  {submitting ? 'Saving…' : 'Link lead & mark sold'}
                </button>
              </div>
            </div>
          )}

          {step === 'link-deal' && (
            <div className="space-y-4">
              <p className="text-sm font-['Manrope'] text-white/60">
                Deals already linked to this vehicle. Sale price and customer info come from the selected deal.
              </p>
              <div className="max-h-64 overflow-y-auto border border-white/10 divide-y divide-white/10">
                {deals.length === 0 ? (
                  <p className="p-4 text-sm text-white/40 font-['Manrope']">No linked deals</p>
                ) : (
                  deals.map((deal) => (
                    <button
                      key={deal.id}
                      type="button"
                      onClick={() => setSelectedDealId(deal.id)}
                      className={`w-full text-left px-3 py-2.5 font-['Manrope'] ${
                        selectedDealId === deal.id ? 'bg-blue-500/20' : 'hover:bg-white/5'
                      }`}
                    >
                      <div className="text-sm text-white">{deal.customer_name || 'Unnamed customer'}</div>
                      <div className="text-xs text-white/45">
                        {(deal.payment_type || 'deal').replace(/_/g, ' ')}
                        {deal.sale_price != null ? ` · $${Number(deal.sale_price).toLocaleString()}` : ''}
                        {deal.customer_phone ? ` · ${deal.customer_phone}` : ''}
                        {deal.created_at ? ` · ${formatDateTimeMdY(deal.created_at)}` : ''}
                      </div>
                    </button>
                  ))
                )}
              </div>
              <div className="flex justify-between pt-2">
                <button type="button" onClick={() => setStep('sold-choice')} className="text-sm text-white/50 font-['Manrope']">
                  Back
                </button>
                <button
                  type="button"
                  disabled={submitting || !selectedDealId}
                  onClick={submitLinkedDeal}
                  className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white font-['Manrope'] font-semibold disabled:opacity-50"
                >
                  {submitting ? 'Saving…' : 'Link deal & mark sold'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
